// src/pages/WhatsappChat.jsx
//
// WhatsApp Chat — invite a lead by phone number, and chat once they tap Yes.
//
//   1. "New chat" sends the invitation template (Yes / No buttons).
//   2. The lead's tap arrives through Meta's webhook; the chat turns active.
//   3. From then on this is a normal chat window: text, photos, video, audio,
//      voice notes, documents — inside WhatsApp's 24-hour reply window.
//
// Several chats can be open side by side (up to three on a wide screen, one on
// a phone); each keeps its own draft, and the list on the left shows unread
// counts for the rest. Everything comes from /api/chat/* — the same API an
// integrating platform uses (whatsapp-broker/docs/whatsapp_chat.md) — so this
// page is also the tester for it.
//
// Updates arrive by polling GET /api/chat/updates?since=<cursor> every few
// seconds: new messages, delivery ticks and status changes for every chat on
// the selected account, in one small request.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle, FiCheck, FiCheckCircle, FiChevronLeft, FiClock, FiFileText, FiMapPin,
  FiMessageCircle, FiMic, FiMoreVertical, FiPaperclip, FiRefreshCw, FiSearch, FiSend,
  FiSquare, FiTrash2, FiUserPlus, FiX,
} from 'react-icons/fi';
import { apiFetch } from '../lib/api';
import { useWabaAccounts, withAccount, accountLabel } from '../lib/waba';

const POLL_MS = 3000;
const PAGE = 100;

// ── small helpers ────────────────────────────────────────────────────────────
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtListTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? fmtTime(iso) : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};
const ago = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const left = (iso) => {
  if (!iso) return '';
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  if (s <= 0) return '';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const fmtSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const initials = (name) => String(name || '?').replace(/^\+/, '').split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
// Numeric, not localeCompare: locale collation ignores the "." and "+" in ISO
// times, and then "12:00:22+00:00" and "12:00:22.91+00:00" sort the wrong way.
const ts = (iso) => Date.parse(iso || '') || 0;
const byTime = (a, b) => ts(a.created_at) - ts(b.created_at) || (a.rev || 0) - (b.rev || 0);

const STATUS = {
  active: { label: 'Active', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  pending: { label: 'Invited', cls: 'border-amber-200 bg-amber-50 text-amber-700' },
  expired: { label: 'No answer', cls: 'border-gray-200 bg-gray-100 text-gray-600' },
  declined: { label: 'Declined', cls: 'border-red-200 bg-red-50 text-red-700' },
  opted_out: { label: 'Opted out', cls: 'border-red-200 bg-red-50 text-red-700' },
  failed: { label: 'Not delivered', cls: 'border-red-200 bg-red-50 text-red-700' },
  closed: { label: 'Ended', cls: 'border-gray-200 bg-gray-100 text-gray-600' },
};
function StatusChip({ status }) {
  const meta = STATUS[status] || { label: status, cls: 'border-gray-200 bg-gray-100 text-gray-600' };
  return <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>{meta.label}</span>;
}

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Invited' },
  { key: 'other', label: 'Other' },
];

function useMaxPanes() {
  const pick = () => (window.innerWidth >= 1536 ? 3 : window.innerWidth >= 1180 ? 2 : 1);
  const [n, setN] = useState(pick);
  useEffect(() => {
    const on = () => setN(pick());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return n;
}

// ── the page ─────────────────────────────────────────────────────────────────
export default function WhatsappChat() {
  const { selected: account, account: accountObj, loading: accountsLoading } = useWabaAccounts();
  const [sessions, setSessions] = useState({});
  const [messages, setMessages] = useState({});          // session id -> [message]
  const [hasOlder, setHasOlder] = useState({});
  const [openIds, setOpenIds] = useState([]);
  const [focusId, setFocusId] = useState(null);
  const [filter, setFilter] = useState('');
  const [query, setQuery] = useState('');
  const [listError, setListError] = useState('');
  const [pollError, setPollError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const cursorRef = useRef(null);          // null until the list has loaded; 0 is a real cursor
  const loadedRef = useRef({});
  const maxPanes = useMaxPanes();

  const mergeSessions = useCallback((list) => {
    if (!list?.length) return;
    setSessions((prev) => {
      const next = { ...prev };
      list.forEach((s) => { if (!next[s.id] || (next[s.id].rev || 0) <= (s.rev || 0)) next[s.id] = s; });
      return next;
    });
  }, []);

  const mergeMessages = useCallback((list, { force = false } = {}) => {
    if (!list?.length) return;
    setMessages((prev) => {
      const next = { ...prev };
      list.forEach((m) => {
        if (!force && !loadedRef.current[m.session_id]) return;   // not open: the list preview is enough
        const arr = (next[m.session_id] || []).filter((x) => x.id !== m.id && !(x._temp && x._tempKey && x._tempKey === m.client_ref));
        arr.push(m);
        arr.sort(byTime);
        next[m.session_id] = arr;
      });
      return next;
    });
  }, []);

  // Load the chat list whenever the account changes. Open chats belong to one
  // account; switching business closes them rather than mixing numbers.
  const loadList = useCallback(async () => {
    if (!account) return;
    setListError('');
    try {
      const out = await apiFetch(withAccount('/chat/sessions?limit=200', account));
      const map = {};
      (out.sessions || []).forEach((s) => { map[s.id] = s; });
      setSessions(map);
      cursorRef.current = out.cursor ?? 0;
    } catch (e) {
      setListError(e.message);
    }
  }, [account]);

  useEffect(() => {
    setSessions({}); setMessages({}); setOpenIds([]); setFocusId(null);
    loadedRef.current = {};
    cursorRef.current = null;
    loadList();
  }, [loadList]);

  // Poll for changes.
  useEffect(() => {
    if (!account) return undefined;
    let stop = false;
    let timer = null;
    let failures = 0;
    const tick = async () => {
      if (stop) return;
      let again = false;
      if (cursorRef.current !== null) {
        try {
          const out = await apiFetch(withAccount(`/chat/updates?since=${cursorRef.current}`, account), { timeoutMs: 15000 });
          if (stop) return;
          cursorRef.current = out.cursor ?? cursorRef.current;
          mergeSessions(out.sessions);
          mergeMessages(out.messages);
          again = out.more;
          failures = 0;
          setPollError('');
        } catch (e) {
          failures += 1;
          if (failures >= 3) setPollError(`Live updates paused: ${e.message}`);
        }
      }
      timer = setTimeout(tick, again ? 50 : document.hidden ? POLL_MS * 3 : POLL_MS);
    };
    timer = setTimeout(tick, POLL_MS);
    return () => { stop = true; clearTimeout(timer); };
  }, [account, mergeSessions, mergeMessages]);

  // Unread count in the tab title, so a new message is noticed from another tab.
  const totalUnread = useMemo(() => Object.values(sessions).reduce((n, s) => n + (s.unread_count || 0), 0), [sessions]);
  useEffect(() => {
    const base = 'Whatsapp Chat';
    const prev = document.title;
    document.title = totalUnread ? `(${totalUnread}) ${base}` : base;
    return () => { document.title = prev; };
  }, [totalUnread]);

  const loadMessages = useCallback(async (id, before) => {
    const out = await apiFetch(`/chat/sessions/${id}/messages?limit=${PAGE}${before ? `&before=${before}` : ''}`);
    loadedRef.current[id] = true;
    setHasOlder((p) => ({ ...p, [id]: (out.messages || []).length >= PAGE }));
    setMessages((prev) => {
      const seen = new Set((prev[id] || []).map((m) => m.id));
      const arr = [...(prev[id] || []), ...(out.messages || []).filter((m) => !seen.has(m.id))];
      arr.sort(byTime);
      return { ...prev, [id]: arr };
    });
  }, []);

  const openChat = useCallback((id) => {
    setFocusId(id);
    setOpenIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      while (next.length > maxPanes) next.shift();
      return next;
    });
    if (!loadedRef.current[id]) loadMessages(id).catch(() => {});
  }, [loadMessages, maxPanes]);

  const closePane = (id) => {
    setOpenIds((prev) => prev.filter((x) => x !== id));
    setFocusId((f) => (f === id ? null : f));
  };

  useEffect(() => {
    setOpenIds((prev) => {
      if (prev.length <= maxPanes) return prev;
      const keep = prev.slice(-maxPanes);
      if (focusId && prev.includes(focusId) && !keep.includes(focusId)) keep[0] = focusId;
      return keep;
    });
  }, [maxPanes, focusId]);

  const upsertSession = useCallback((s) => { if (s) mergeSessions([s]); }, [mergeSessions]);

  const onDeleted = (id) => {
    closePane(id);
    setSessions((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(sessions)
      .filter((s) => {
        if (filter === 'active' && s.status !== 'active') return false;
        if (filter === 'pending' && s.status !== 'pending') return false;
        if (filter === 'other' && ['active', 'pending'].includes(s.status)) return false;
        if (!q) return true;
        return [s.display_name, s.phone, s.lead_name, s.profile_name, s.client_ref].some((v) => String(v || '').toLowerCase().includes(q));
      })
      .sort((a, b) => ts(b.last_message?.at || b.created_at) - ts(a.last_message?.at || a.created_at));
  }, [sessions, filter, query]);

  const singlePane = maxPanes === 1;
  const showList = !singlePane || openIds.length === 0;

  return (
    <div className="flex h-[calc(100vh-64px)] min-h-[560px] flex-col p-3 sm:p-4 lg:h-[calc(100vh-76px)] lg:px-8 lg:pb-6 lg:pt-2">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <FiMessageCircle className="text-2xl text-brand-600" />
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-ink">Whatsapp Chat</h1>
          <p className="truncate text-xs text-gray-500">
            Invite a lead by number; the chat opens when they tap <b>Yes</b>.
            {accountObj && <> Sending from <b>{accountLabel(accountObj)}</b>.</>}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          disabled={!account}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <FiUserPlus /> New chat
        </button>
      </div>

      {pollError && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <FiAlertCircle /> {pollError}
        </div>
      )}

      <div className="flex min-h-0 flex-1 gap-3">
        {showList && (
          <aside className={`flex min-h-0 flex-col rounded-xl border bg-white ${singlePane ? 'w-full' : 'w-80 shrink-0'}`}>
            <ConsentTemplateCard account={account} />
            <div className="border-b p-3">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name or number"
                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div className="mt-2 flex gap-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === f.key ? 'bg-brand-100 text-brand-800' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {f.label}
                  </button>
                ))}
                <button type="button" onClick={loadList} title="Reload" className="ml-auto rounded-full p-1.5 text-gray-500 hover:bg-gray-100">
                  <FiRefreshCw />
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {listError && <p className="p-4 text-sm text-red-600">{listError}</p>}
              {!listError && !accountsLoading && list.length === 0 && (
                <div className="p-6 text-center text-sm text-gray-500">
                  {Object.keys(sessions).length ? 'No chats match.' : 'No chats yet. Start one with New chat.'}
                </div>
              )}
              {list.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => openChat(s.id)}
                  className={`flex w-full items-start gap-3 border-b px-3 py-3 text-left hover:bg-gray-50 ${openIds.includes(s.id) ? 'bg-brand-50' : ''}`}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-bold text-gray-700">
                    {initials(s.display_name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-ink">{s.display_name}</span>
                      <span className="ml-auto shrink-0 text-[11px] text-gray-400">{fmtListTime(s.last_message?.at || s.created_at)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span className="truncate text-xs text-gray-500">
                        {s.last_message?.direction === 'outbound' && 'You: '}
                        {s.last_message?.preview || `+${s.phone}`}
                      </span>
                      {s.unread_count > 0 && (
                        <span className="ml-auto shrink-0 rounded-full bg-brand-600 px-1.5 text-[11px] font-bold text-white">{s.unread_count}</span>
                      )}
                    </div>
                    <div className="mt-1"><StatusChip status={s.status} /></div>
                  </div>
                </button>
              ))}
            </div>
          </aside>
        )}

        {(!singlePane || openIds.length > 0) && (
          <section className="flex min-h-0 min-w-0 flex-1 gap-3">
            {openIds.length === 0 && (
              <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed bg-white p-8 text-center text-gray-500">
                <FiMessageCircle className="mb-3 text-4xl text-gray-300" />
                <p className="text-sm">Pick a chat on the left, or start a new one.</p>
                <p className="mt-1 text-xs">Up to {maxPanes} chats can be open side by side on this screen.</p>
              </div>
            )}
            {openIds.map((id) => sessions[id] && (
              <ChatPane
                key={id}
                session={sessions[id]}
                messages={messages[id]}
                hasOlder={!!hasOlder[id]}
                focused={focusId === id}
                singlePane={singlePane}
                onFocus={() => setFocusId(id)}
                onClose={() => closePane(id)}
                onLoadOlder={(before) => loadMessages(id, before)}
                onSession={upsertSession}
                onMessages={(list) => mergeMessages(list, { force: true })}
                onDeleted={() => onDeleted(id)}
              />
            ))}
          </section>
        )}
      </div>

      {showNew && (
        <NewChatDialog
          account={account}
          onClose={() => setShowNew(false)}
          onOpened={(s) => { upsertSession(s); openChat(s.id); setShowNew(false); }}
        />
      )}
    </div>
  );
}

// ── invitation template status ───────────────────────────────────────────────
function ConsentTemplateCard({ account }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!account) return;
    setError('');
    try {
      setCfg(await apiFetch(withAccount('/chat/config', account)));
    } catch (e) {
      setError(e.message);
    }
  }, [account]);
  useEffect(() => { setCfg(null); load(); }, [load]);

  const create = async () => {
    setBusy(true); setError('');
    try {
      await apiFetch('/chat/consent-template', { method: 'POST', body: JSON.stringify({ waba_account: account }) });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const t = cfg?.consent_template;
  const ready = t?.found && t?.status === 'APPROVED' && !t?.problem;
  return (
    <div className={`border-b px-3 py-2 text-xs ${ready ? 'bg-white' : 'bg-amber-50'}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-left">
        {ready ? <FiCheckCircle className="text-emerald-600" /> : <FiAlertCircle className="text-amber-600" />}
        <span className="flex-1 truncate font-semibold text-gray-700">
          {!cfg && !error && 'Checking invitation template…'}
          {error && 'Could not check the invitation template'}
          {cfg && (ready ? `Invitation template ready` : t?.found ? `Invitation template ${t.status}` : 'No invitation template yet')}
        </span>
        <span className="text-gray-400">{open ? 'Hide' : 'Details'}</span>
      </button>
      {(open || (cfg && !ready)) && (
        <div className="mt-2 space-y-2 text-gray-600">
          {error && <p className="text-red-600">{error}</p>}
          {t && (
            <p>
              <b>{t.name}</b> ({t.language})
              {t.found && t.buttons?.length > 0 && <> · buttons: {t.buttons.join(' / ')}</>}
            </p>
          )}
          {t?.body && <p className="rounded bg-gray-50 p-2 italic">{t.body}</p>}
          {t?.problem && <p className="text-amber-800">{t.problem}</p>}
          {cfg && !t?.found && (
            <button type="button" onClick={create} disabled={busy} className="rounded-lg bg-ink px-3 py-1.5 font-semibold text-white disabled:opacity-50">
              {busy ? 'Submitting…' : 'Create the standard invitation template'}
            </button>
          )}
          {t?.found && t.status !== 'APPROVED' && (
            <p>Meta is reviewing it — usually a few minutes. <button type="button" onClick={load} className="font-semibold text-brand-700 underline">Check again</button></p>
          )}
          {cfg && <p className="text-gray-500">Unanswered invitations show as “No answer” after {cfg.invite_ttl_hours}h. Replies are possible for 24h after the lead's last message.</p>}
        </div>
      )}
    </div>
  );
}

// ── new chat ─────────────────────────────────────────────────────────────────
function NewChatDialog({ account, onClose, onOpened }) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [welcome, setWelcome] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [template, setTemplate] = useState('');
  const [language, setLanguage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);   // {code, message, session}
  const [note, setNote] = useState('');

  const submit = async (extra = {}) => {
    setBusy(true); setError(''); setNote('');
    try {
      const body = { phone, waba_account: account, lead_name: name || undefined, ...extra };
      if (welcome.trim()) body.welcome_message = welcome.trim();
      if (template.trim()) body.template_name = template.trim();
      if (language.trim()) body.language_code = language.trim();
      const out = await apiFetch('/chat/sessions', { method: 'POST', body: JSON.stringify(body), timeoutMs: 45000 });
      if (!out.invite_sent && out.note) {
        setNote(out.note);
        setTimeout(() => onOpened(out.session), 1200);
      } else {
        onOpened(out.session);
      }
    } catch (e) {
      // 409s for leads who said no before: ask, then retry with reinvite.
      if (/declined|asked to stop/.test(e.message)) setConfirm({ message: e.message });
      else setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
      >
        <div className="mb-4 flex items-center">
          <h2 className="flex-1 text-lg font-bold text-ink">New chat</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Close"><FiX /></button>
        </div>
        <p className="mb-4 text-sm text-gray-600">
          The lead gets an invitation with <b>Yes</b> / <b>No</b> buttons. The chat opens here as soon as they tap Yes.
        </p>
        <label className="mb-3 block text-sm">
          <span className="font-medium text-gray-700">WhatsApp number *</span>
          <input
            autoFocus required value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel"
            placeholder="+91 98123 45678"
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-brand-500"
          />
          <span className="text-xs text-gray-500">With country code. A 10-digit number is taken as Indian (+91).</span>
        </label>
        <label className="mb-3 block text-sm">
          <span className="font-medium text-gray-700">Lead name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Asha"
                 className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-brand-500" />
          <span className="text-xs text-gray-500">Used in the invitation (“Hi Asha, …”).</span>
        </label>
        <label className="mb-3 block text-sm">
          <span className="font-medium text-gray-700">Welcome message</span>
          <textarea value={welcome} onChange={(e) => setWelcome(e.target.value)} rows={2}
                    placeholder="Optional — sent automatically the moment they tap Yes"
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-brand-500" />
        </label>
        <button type="button" onClick={() => setAdvanced((a) => !a)} className="mb-2 text-xs font-semibold text-brand-700">
          {advanced ? 'Hide' : 'Use a different'} invitation template
        </button>
        {advanced && (
          <div className="mb-3 grid grid-cols-2 gap-2">
            <input value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="template name"
                   className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="language, e.g. en_US"
                   className="rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <p className="col-span-2 text-xs text-gray-500">Must be APPROVED on this account and have a Yes quick-reply button.</p>
          </div>
        )}
        {error && <p className="mb-3 rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</p>}
        {note && <p className="mb-3 rounded-lg bg-gray-50 p-2 text-sm text-gray-700">{note}</p>}
        {confirm && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p>{confirm.message}</p>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => { setConfirm(null); submit({ reinvite: true }); }}
                      className="rounded-lg bg-amber-600 px-3 py-1.5 font-semibold text-white">They asked us to — invite again</button>
              <button type="button" onClick={() => setConfirm(null)} className="rounded-lg px-3 py-1.5 font-semibold text-amber-900">Cancel</button>
            </div>
          </div>
        )}
        <button type="submit" disabled={busy || !phone.trim()}
                className="w-full rounded-lg bg-brand-600 py-2.5 font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
          {busy ? 'Sending invitation…' : 'Send invitation'}
        </button>
      </form>
    </div>
  );
}

// ── one chat window ──────────────────────────────────────────────────────────
function ChatPane({ session, messages, hasOlder, focused, singlePane, onFocus, onClose, onLoadOlder,
                    onSession, onMessages, onDeleted }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [pending, setPending] = useState([]);        // optimistic bubbles
  const [recording, setRecording] = useState(null);   // {recorder, started}
  const [recSecs, setRecSecs] = useState(0);
  const [, tick] = useState(0);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const atBottomRef = useRef(true);
  const readingRef = useRef(false);
  const loading = messages === undefined;

  // Re-render every 30s so "window closes in …" and "sent 5m ago" stay true.
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);

  const all = useMemo(() => [...(messages || []), ...pending], [messages, pending]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [all.length]);

  // Opening a chat, or a new message landing in an open one, marks it read.
  useEffect(() => {
    if (!session.unread_count || readingRef.current || document.hidden) return;
    readingRef.current = true;
    apiFetch(`/chat/sessions/${session.id}/read`, { method: 'POST' })
      .then((out) => onSession(out.session))
      .catch(() => {})
      .finally(() => { readingRef.current = false; });
  }, [session.unread_count, session.id, onSession]);

  const act = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const invite = (flags) => act(async () => {
    const out = await apiFetch('/chat/sessions', {
      method: 'POST', timeoutMs: 45000,
      body: JSON.stringify({ phone: session.phone, waba_account: session.waba_account, ...flags }),
    });
    onSession(out.session);
    if (out.message) onMessages([out.message]);
  });

  const sendText = async () => {
    const text = draft.trim();
    if (!text) return;
    const key = `tmp-${Date.now()}`;
    setDraft('');
    setError('');
    setPending((p) => [...p, { id: key, _temp: true, direction: 'outbound', type: 'text', text, status: 'sending', created_at: new Date().toISOString() }]);
    try {
      const out = await apiFetch(`/chat/sessions/${session.id}/messages`, { method: 'POST', body: JSON.stringify({ type: 'text', text }) });
      onMessages([out.message]);
      onSession(out.session);
    } catch (e) {
      setError(e.message);
      setDraft((d) => d || text);
    } finally {
      setPending((p) => p.filter((m) => m.id !== key));
    }
  };

  const sendFile = async (file, extra = {}) => {
    if (!file) return;
    const key = `tmp-${Date.now()}`;
    const caption = extra.caption ?? draft.trim();
    if (extra.caption === undefined) setDraft('');
    setError('');
    setPending((p) => [...p, { id: key, _temp: true, direction: 'outbound', type: 'document', text: caption,
                               media: { filename: file.name, size: file.size }, status: 'sending',
                               created_at: new Date().toISOString() }]);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      if (caption) fd.append('caption', caption);
      if (extra.type) fd.append('type', extra.type);
      const out = await apiFetch(`/chat/sessions/${session.id}/media`, { method: 'POST', body: fd, timeoutMs: 180000 });
      onMessages([out.message]);
      onSession(out.session);
      if (out.message?.note) setError(out.message.note);
    } catch (e) {
      setError(e.message);
    } finally {
      setPending((p) => p.filter((m) => m.id !== key));
    }
  };

  // Voice notes: whatever the browser records (Firefox: OGG/Opus, Chrome:
  // WebM/Opus); the server converts WebM to OGG so it plays as a voice note.
  const startRecording = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('This browser cannot record audio.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
        .find((m) => MediaRecorder.isTypeSupported(m)) || '';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recorder._cancelled || !chunks.length) return;
        const type = recorder.mimeType || mime || 'audio/webm';
        const ext = type.includes('ogg') ? 'ogg' : type.includes('mp4') ? 'm4a' : 'webm';
        const blob = new Blob(chunks, { type });
        sendFile(new File([blob], `voice-note-${Date.now()}.${ext}`, { type: type.split(';')[0] }), { type: 'audio', caption: '' });
      };
      recorder.start();
      setRecording({ recorder, started: Date.now() });
    } catch (e) {
      setError(e?.name === 'NotAllowedError' ? 'Microphone permission was denied.' : `Could not start recording: ${e.message}`);
    }
  };
  useEffect(() => {
    if (!recording) return undefined;
    const t = setInterval(() => setRecSecs(Math.floor((Date.now() - recording.started) / 1000)), 250);
    return () => clearInterval(t);
  }, [recording]);
  const stopRecording = (cancel = false) => {
    if (!recording) return;
    recording.recorder._cancelled = cancel;
    recording.recorder.stop();
    setRecording(null);
    setRecSecs(0);
  };

  const s = session;
  const canType = s.can_send?.free_form;
  const windowLeft = s.status === 'active' && s.window?.open ? left(s.window.expires_at) : '';

  const banner = (() => {
    const who = s.lead_name || s.profile_name || 'The lead';
    switch (s.status) {
      case 'pending':
        return { tone: 'amber', text: `Invitation sent ${ago(s.invite?.sent_at)}. Waiting for ${who} to tap Yes.`,
                 action: 'Resend invitation', run: () => invite({ resend: true }) };
      case 'expired':
        return { tone: 'gray', text: `No answer to the invitation sent ${ago(s.invite?.sent_at)}.`,
                 action: 'Send again', run: () => invite({}) };
      case 'declined':
        return { tone: 'red', text: `${who} declined the invitation.`,
                 action: 'Invite again', run: () => window.confirm(`${who} said no. Only invite again if they asked you to. Continue?`) && invite({ reinvite: true }) };
      case 'opted_out':
        return { tone: 'red', text: `${who} asked to stop receiving messages.`,
                 action: 'Invite again', run: () => window.confirm(`${who} opted out. Only invite again if they asked you to. Continue?`) && invite({ reinvite: true }) };
      case 'failed':
        return { tone: 'red', text: `The invitation was not delivered. ${s.last_error || ''}`,
                 action: 'Try again', run: () => invite({}) };
      case 'closed':
        return { tone: 'gray', text: 'This chat was ended. A new invitation asks the lead again.',
                 action: 'Invite again', run: () => invite({}) };
      case 'active':
        if (!s.window?.open) {
          return { tone: 'amber', text: 'WhatsApp’s 24-hour reply window has closed — only a template can be sent. Resending the invitation asks the lead to reply, which reopens it.',
                   action: 'Resend invitation', run: () => invite({ resend: true }) };
        }
        return null;
      default:
        return null;
    }
  })();
  const toneCls = { amber: 'border-amber-200 bg-amber-50 text-amber-900', red: 'border-red-200 bg-red-50 text-red-800', gray: 'border-gray-200 bg-gray-50 text-gray-700' };

  return (
    <div
      onMouseDown={onFocus}
      className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-white ${focused && !singlePane ? 'border-brand-300 ring-1 ring-brand-200' : ''}`}
    >
      {/* header */}
      <div className="flex items-center gap-2 border-b px-3 py-2">
        {singlePane && (
          <button type="button" onClick={onClose} className="-ml-1 rounded-lg p-2 text-gray-600 hover:bg-gray-100" aria-label="Back"><FiChevronLeft /></button>
        )}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-700">{initials(s.display_name)}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-ink">{s.display_name}</span>
            <StatusChip status={s.status} />
          </div>
          <div className="truncate text-xs text-gray-500">
            +{s.wa_id || s.phone}
            {s.profile_name && s.lead_name && s.profile_name !== s.lead_name && <> · WhatsApp name “{s.profile_name}”</>}
            {windowLeft && <> · <FiClock className="-mt-0.5 inline" /> replies open {windowLeft}</>}
          </div>
        </div>
        <div className="relative">
          <button type="button" onClick={() => setMenu((m) => !m)} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100" aria-label="Chat actions"><FiMoreVertical /></button>
          {menu && (
            <div className="absolute right-0 z-20 mt-1 w-52 rounded-lg border bg-white py-1 text-sm shadow-lg" onMouseLeave={() => setMenu(false)}>
              {['pending', 'active', 'expired', 'failed', 'closed'].includes(s.status) && (
                <button type="button" className="block w-full px-3 py-2 text-left hover:bg-gray-50"
                        onClick={() => { setMenu(false); invite({ resend: true }); }}>Resend invitation</button>
              )}
              {s.status !== 'closed' && (
                <button type="button" className="block w-full px-3 py-2 text-left hover:bg-gray-50"
                        onClick={() => { setMenu(false); act(async () => onSession((await apiFetch(`/chat/sessions/${s.id}/close`, { method: 'POST' })).session)); }}>
                  End chat
                </button>
              )}
              <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setMenu(false);
                        if (!window.confirm(`Delete this chat and its whole history with ${s.display_name}? This cannot be undone.`)) return;
                        act(async () => { await apiFetch(`/chat/sessions/${s.id}`, { method: 'DELETE' }); onDeleted(); });
                      }}>
                <FiTrash2 /> Delete chat
              </button>
            </div>
          )}
        </div>
        {!singlePane && (
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100" aria-label="Close chat window"><FiX /></button>
        )}
      </div>

      {/* messages */}
      <div
        ref={scrollRef}
        onScroll={(e) => { const el = e.currentTarget; atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}
        className="min-h-0 flex-1 space-y-1 overflow-y-auto bg-[#f5f1eb] px-3 py-3"
      >
        {loading && <p className="py-6 text-center text-sm text-gray-500">Loading…</p>}
        {hasOlder && !loading && (
          <div className="text-center">
            <button type="button" onClick={() => { atBottomRef.current = false; onLoadOlder(messages[0]?.id).catch(() => {}); }}
                    className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 shadow-sm">Load earlier messages</button>
          </div>
        )}
        {all.map((m, i) => {
          const day = fmtDay(m.created_at);
          const showDay = i === 0 || fmtDay(all[i - 1].created_at) !== day;
          return (
            <React.Fragment key={m.id}>
              {showDay && <div className="py-2 text-center"><span className="rounded-md bg-white/80 px-2 py-0.5 text-[11px] text-gray-500 shadow-sm">{day}</span></div>}
              <Bubble m={m} all={all} />
            </React.Fragment>
          );
        })}
      </div>

      {/* state + composer */}
      {banner && (
        <div className={`flex flex-wrap items-center gap-2 border-t px-3 py-2 text-xs ${toneCls[banner.tone]}`}>
          <span className="min-w-0 flex-1">{banner.text}</span>
          <button type="button" disabled={busy} onClick={banner.run}
                  className="rounded-lg bg-white px-2.5 py-1 font-semibold shadow-sm ring-1 ring-black/10 disabled:opacity-50">
            {busy ? '…' : banner.action}
          </button>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 border-t bg-red-50 px-3 py-2 text-xs text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" /><span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Dismiss"><FiX /></button>
        </div>
      )}
      <div className="flex items-end gap-2 border-t bg-white p-2">
        <input ref={fileRef} type="file" className="hidden"
               accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
               onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) sendFile(f); }} />
        {recording ? (
          <div className="flex flex-1 items-center gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" />
            Recording {String(Math.floor(recSecs / 60)).padStart(2, '0')}:{String(recSecs % 60).padStart(2, '0')}
            <button type="button" onClick={() => stopRecording(true)} className="ml-auto text-xs font-semibold underline">Cancel</button>
            <button type="button" onClick={() => stopRecording(false)} className="rounded-full bg-red-600 p-2 text-white" aria-label="Stop and send"><FiSquare /></button>
          </div>
        ) : (
          <>
            <button type="button" disabled={!canType} onClick={() => fileRef.current?.click()} title="Attach a photo, video, audio or document"
                    className="rounded-full p-2.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40"><FiPaperclip /></button>
            <textarea
              value={draft}
              disabled={!canType}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
              onPaste={(e) => { const f = e.clipboardData?.files?.[0]; if (f && canType) { e.preventDefault(); sendFile(f); } }}
              rows={1}
              placeholder={canType ? 'Type a message' : 'Messaging is not open yet'}
              title={canType ? 'Enter sends · Shift+Enter adds a new line · paste an image to send it' : undefined}
              className="max-h-32 min-h-[42px] flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
            />
            {draft.trim() ? (
              <button type="button" onClick={sendText} disabled={!canType} className="rounded-full bg-brand-600 p-2.5 text-white hover:bg-brand-700 disabled:opacity-40" aria-label="Send"><FiSend /></button>
            ) : (
              <button type="button" onClick={startRecording} disabled={!canType} className="rounded-full bg-brand-600 p-2.5 text-white hover:bg-brand-700 disabled:opacity-40" aria-label="Record a voice note" title="Record a voice note"><FiMic /></button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── one message ──────────────────────────────────────────────────────────────
function Ticks({ status, error }) {
  if (status === 'sending') return <FiClock className="text-gray-400" title="Sending" />;
  if (status === 'failed') return <FiAlertCircle className="text-red-600" title={error || 'Failed'} />;
  if (status === 'sent') return <FiCheck className="text-gray-400" title="Sent" />;
  const cls = status === 'read' ? 'text-brand-600' : 'text-gray-400';
  if (status === 'delivered' || status === 'read') {
    return (
      <span className={`relative inline-flex w-4 ${cls}`} title={status === 'read' ? 'Read' : 'Delivered'}>
        <FiCheck /><FiCheck className="absolute left-1" />
      </span>
    );
  }
  return null;
}

function Media({ m }) {
  const media = m.media || {};
  const url = media.url;
  if (!url) {
    if (m.type === 'document' || media.filename) {
      return <div className="flex items-center gap-2 rounded bg-black/5 p-2 text-sm"><FiFileText /> {media.filename || 'File'} {media.size ? `· ${fmtSize(media.size)}` : ''}</div>;
    }
    return <p className="text-xs italic text-gray-500">[{m.type} not available]</p>;
  }
  if (m.type === 'image') return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={m.text || 'image'} className="max-h-64 rounded-md" /></a>;
  if (m.type === 'sticker') return <img src={url} alt="sticker" className="h-28 w-28 object-contain" />;
  if (m.type === 'video') return <video src={url} controls className="max-h-64 rounded-md" />;
  if (m.type === 'audio') return <audio src={url} controls className="w-60 max-w-full" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded bg-black/5 p-2 text-sm hover:bg-black/10">
      <FiFileText className="shrink-0 text-lg" />
      <span className="min-w-0 truncate font-medium">{media.filename || url.split('/').pop()}</span>
      {media.size ? <span className="shrink-0 text-xs text-gray-500">{fmtSize(media.size)}</span> : null}
    </a>
  );
}

function Bubble({ m, all }) {
  if (m.direction === 'system') {
    return (
      <div className="py-1 text-center">
        <span className="inline-block max-w-[90%] rounded-md bg-white/90 px-2 py-1 text-[11px] text-gray-600 shadow-sm">{m.text} · {fmtTime(m.created_at)}</span>
      </div>
    );
  }
  const out = m.direction === 'outbound';
  const quoted = m.reply_to ? all.find((x) => x.wamid === m.reply_to) : null;
  const loc = m.type === 'location' && m.payload?.latitude != null ? `https://maps.google.com/?q=${m.payload.latitude},${m.payload.longitude}` : null;
  const isMedia = ['image', 'video', 'audio', 'document', 'sticker'].includes(m.type);
  return (
    <div className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
      <div className={`relative max-w-[85%] rounded-lg px-2.5 py-1.5 shadow-sm ${out ? 'bg-[#e1f7d7]' : 'bg-white'} ${m._temp ? 'opacity-70' : ''}`}>
        {m.type === 'template' && (
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{m.payload?.role === 'invite' ? 'Invitation' : 'Template'}</div>
        )}
        {quoted && (
          <div className="mb-1 truncate rounded border-l-4 border-brand-400 bg-black/5 px-2 py-1 text-xs text-gray-600">{quoted.text || `[${quoted.type}]`}</div>
        )}
        {isMedia && <div className="mb-1"><Media m={m} /></div>}
        {loc ? (
          <a href={loc} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-brand-700 underline"><FiMapPin /> {m.text}</a>
        ) : (
          m.text && <p className="whitespace-pre-wrap break-words text-sm text-gray-900">{m.text}</p>
        )}
        {m.type === 'template' && m.payload?.buttons?.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {m.payload.buttons.map((b) => <span key={b} className="rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-brand-700">{b}</span>)}
          </div>
        )}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-gray-500">
          {out && m.sent_by === 'system' && <span>auto ·</span>}
          {fmtTime(m.created_at)}
          {out && <Ticks status={m.status} error={m.error} />}
        </div>
        {m.status === 'failed' && m.error && <p className="mt-1 max-w-xs text-[11px] text-red-700">{m.error}</p>}
        {m.reaction && (
          <span className={`absolute -bottom-3 ${out ? 'left-1' : 'right-1'} rounded-full bg-white px-1 text-sm shadow`}>{m.reaction}</span>
        )}
      </div>
    </div>
  );
}
